import { useState, useEffect } from "react";
import { callFinnhub } from "./finnhubClient";

// No key here. Every Finnhub call goes through the Netlify proxy, which holds
// the key server-side — see netlify/functions/finnhub.js for why.
const _cache = {};

const TTL_PRICE = 5  * 60 * 1000;       // 5 min
const TTL_FUND  = 24 * 60 * 60 * 1000;  // 24 h
const TTL_FX    = 60 * 60 * 1000;       // 60 min

// The browser-side cache stays exactly as it was: the proxy has its own cache,
// but this one avoids the round trip entirely and is what keeps a re-render
// from costing anything.
async function _get(cacheKey, ttlMs, load) {
  const now = Date.now();
  const hit = _cache[cacheKey];
  if (hit && now - hit.ts < ttlMs) return hit.data;
  const data = await load();
  if (data != null) _cache[cacheKey] = { data, ts: now };
  return data;
}

// Plain fetch, for the one source that needs no key and no proxy.
async function _getOpen(cacheKey, url, ttlMs) {
  return _get(cacheKey, ttlMs, async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn(`[Finnhub] ${cacheKey}:`, e.message);
      return null;
    }
  });
}

export async function fetchQuote(symbol) {
  return _get(`q_${symbol}`, TTL_PRICE, async () => {
    const d = await callFinnhub("quote", { symbols: [symbol] });
    return d?.quotes?.[String(symbol).toUpperCase()] ?? null;
  });
}

/**
 * Several quotes in one request.
 *
 * This is the reason the proxy is not slower than the direct calls it replaces:
 * a ten-holding portfolio was ten browser requests and is now one. Symbols
 * already in the local cache are not asked for again.
 */
export async function fetchQuotes(symbols = []) {
  const now = Date.now();
  const out = {};
  const missing = [];
  for (const s of symbols) {
    const hit = _cache[`q_${s}`];
    if (hit && now - hit.ts < TTL_PRICE) out[s] = hit.data;
    else missing.push(s);
  }
  if (missing.length) {
    const d = await callFinnhub("quote", { symbols: missing });
    for (const s of missing) {
      const q = d?.quotes?.[String(s).toUpperCase()] ?? null;
      out[s] = q;
      if (q != null) _cache[`q_${s}`] = { data: q, ts: now };
    }
  }
  return out;
}

export async function fetchProfile(symbol) {
  return _get(`p_${symbol}`, TTL_FUND, async () =>
    (await callFinnhub("profile", { symbol }))?.profile ?? null);
}

export async function fetchMetrics(symbol) {
  return _get(`m_${symbol}`, TTL_FUND, async () =>
    (await callFinnhub("metrics", { symbol }))?.metrics ?? null);
}

// Exchange rates — ExchangeRate-API (no key needed, 60-min cache)
export async function fetchExchangeRates() {
  return _getOpen('fx_usd', 'https://open.er-api.com/v6/latest/USD', TTL_FX);
}

export async function fetchNews(symbol) {
  const to   = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return _get(`n_${symbol}`, TTL_PRICE, async () =>
    (await callFinnhub("companyNews", { symbol, from, to }))?.news ?? null);
}

// ── React hooks ───────────────────────────────────────────────────────────────

export function useStockMetrics(symbol) {
  const [m, setM] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchMetrics(symbol).then(d => {
      if (alive && d?.metric) setM(d.metric);
    });
    return () => { alive = false; };
  }, [symbol]);
  return {
    pe:            m?.peBasicExclExtraTTM          ?? m?.peTTM             ?? null,
    dividendYield: m?.dividendYieldIndicatedAnnual                          ?? null,
    high52:        m?.["52WeekHigh"]                                        ?? null,
    low52:         m?.["52WeekLow"]                                         ?? null,
    beta:          m?.beta                                                  ?? null,
    eps:           m?.epsBasicExclExtraItemsTTM                             ?? null,
    marketCap:     m?.marketCapitalization                                  ?? null,
    isLive:        !!m,
  };
}

export function useCompanyProfile(symbol) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchProfile(symbol).then(d => {
      if (alive && d?.name) setProfile(d);
    });
    return () => { alive = false; };
  }, [symbol]);
  return profile;
}

export function useCompanyNews(symbol) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchNews(symbol).then(d => {
      if (!alive) return;
      setNews(Array.isArray(d) ? d.slice(0, 10) : []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [symbol]);
  return { news, loading };
}
